// components/token/TokenChartV2.js
// GeckoTerminal iframe-based chart implementation
import React, { useMemo } from "react";

const TokenChartV2 = ({ pair, timeFrame, onTimeFrameChange }) => {
  console.log("🎨 [TokenChartV2] Rendering with:", {
    pair: pair,
    hasPair: !!pair,
    pairAddress: pair?.pairAddress,
    chainId: pair?.chainId,
    timeFrame: timeFrame
  });

  // Map our timeframe to GeckoTerminal resolution format
  const timeframeMap = {
    "1m": "1m",
    "5m": "5m",
    "15m": "15m",
    "1h": "1h",
    "4h": "4h",
    "1d": "1d",
  };

  // Convert chainId to GeckoTerminal chain slug format
  const getGeckoTerminalChain = (chainId) => {
    // GeckoTerminal chain slugs
    const chainMap = {
      solana: "solana",
      "0x1": "eth", // Ethereum
      ethereum: "eth",
      "0x38": "bsc", // BSC
      bsc: "bsc",
      binance: "bsc",
      "0x89": "polygon", // Polygon
      polygon: "polygon",
      "0xa4b1": "arbitrum", // Arbitrum
      arbitrum: "arbitrum",
      "0x2105": "base", // Base
      base: "base",
      "0xa": "optimism", // Optimism
      optimism: "optimism",
      "0xa86a": "avalanche", // Avalanche
      avalanche: "avalanche",
      "0xfa": "fantom", // Fantom
      fantom: "fantom",
      "0x171": "pulse", // Pulse
      pulse: "pulse",
    };

    // Check if it's Solana
    if (chainId === "solana") {
      return "solana";
    }

    // Return mapped chain or default to eth
    return chainMap[chainId] || "eth";
  };

  // Build the GeckoTerminal iframe URL dynamically
  const iframeUrl = useMemo(() => {
    if (!pair || !pair.pairAddress) {
      console.warn("⚠️ [TokenChartV2] Cannot build iframe URL - missing pair or pairAddress:", {
        hasPair: !!pair,
        pairAddress: pair?.pairAddress,
        chainId: pair?.chainId
      });
      return null;
    }

    const chain = getGeckoTerminalChain(pair.chainId);
    const resolution = timeframeMap[timeFrame] || "1m";
    const pairAddress = pair.pairAddress;

    // GeckoTerminal URL format: https://www.geckoterminal.com/{chain}/pools/{pairAddress}?params
    const baseUrl = `https://www.geckoterminal.com/${chain}/pools/${pairAddress}`;
    const params = new URLSearchParams({
      embed: "1",
      info: "0",
      swaps: "0",
      light_chart: "0",
      chart_type: "market_cap",
      resolution: resolution,
      bg_color: "111827", // Dark background to match our theme
    });

    const fullUrl = `${baseUrl}?${params.toString()}`;
    console.log("🔗 [TokenChartV2] Generated dynamic iframe URL:", {
      chain: chain,
      pairAddress: pairAddress,
      resolution: resolution,
      fullUrl: fullUrl
    });
    
    return fullUrl;
  }, [pair, timeFrame]);

  // Show loading state if no pair or pairAddress
  if (!pair || !pair.pairAddress) {
    return (
      <div className="h-full flex items-center justify-center text-white/80 bg-[#0a0e1a] rounded-xl border border-[rgba(255,255,255,0.1)]">
        <div className="text-center p-8">
          <div className="text-2xl mb-4">📊</div>
          <div className="text-lg mb-2 font-semibold">Waiting for pair data...</div>
          <div className="text-sm text-white/60">
            {!pair ? "Fetching pair information from DexScreener..." : !pair.pairAddress ? "Pair address not found. The token may not have trading pairs yet." : "Unknown error"}
          </div>
        </div>
      </div>
    );
  }

  if (!iframeUrl) {
    return (
      <div className="h-full flex items-center justify-center text-white/80 bg-[#0a0e1a] rounded-xl border border-[rgba(255,255,255,0.1)]">
        <div className="text-center p-8">
          <div className="text-lg mb-2 font-semibold">Building chart URL...</div>
        </div>
      </div>
    );
  }

  // Render the GeckoTerminal iframe dynamically
  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <iframe
        id="geckoterminal-embed"
        title="GeckoTerminal Embed"
        src={iframeUrl}
        frameBorder="0"
        allow="clipboard-write"
        allowFullScreen
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
};

export default TokenChartV2;
